import { useEffect, useState, useCallback, useMemo } from "react";
import { api, apiErr } from "../lib/api";
import { useAppData } from "../context/AppDataContext";
import { toast } from "sonner";
import {
  Trash2, Plus, Check, Power, Ban, ShieldCheck, UserCog, Building2,
  Save, MessageCircle, Image as ImageIcon, Loader2, KeyRound, Pencil, Clock, Send, FileSpreadsheet, FileText, BadgeCheck, Languages,
} from "lucide-react";
import { fmtStamp } from "../lib/datetime";
import { LanguageFields } from "./LanguageFields";
import { InterpreterReports } from "./InterpreterReports";
import { exportExcel, exportPDF } from "../lib/exporters";

const inp = "w-full border border-line bg-white rounded-md px-3 py-2 text-sm outline-none focus:border-clinical";
const ROLE_LABEL = { system_admin: "System Admin", super_admin: "Super Admin", admin: "Administrator", clinical_specialist: "Clinical Specialist", language_interpreter: "Language Interpreter", public: "Basic User" };
const STATUS_STYLE = {
  active: "text-calm bg-calm/10", pending: "text-alert bg-alert/10",
  deactivated: "text-muted2 bg-line", revoked: "text-critical bg-critical/10",
};

/* ---------------- Users (FR-001) ---------------- */
function ClinicAccessFields({ clinics, clinicIds = [], allClinics = false, onChange }) {
  const toggle = (id) => (clinicIds.includes(id) ? clinicIds.filter((x) => x !== id) : [...clinicIds, id]);
  return (
    <div className="sm:col-span-2 border border-line rounded-md p-3 bg-sand/40" data-testid="clinic-access-fields">
      <span className="text-xs uppercase tracking-wider font-bold text-muted2">Clinic access</span>
      <label className="flex items-center gap-2 text-sm mt-2">
        <input type="checkbox" data-testid="all-clinics-toggle" checked={allClinics} onChange={(e) => onChange({ clinic_ids: clinicIds, all_clinics: e.target.checked })} />
        Grant access to <b>all clinics</b>
      </label>
      {!allClinics && (
        <div className="flex flex-wrap gap-2 mt-2">
          {(clinics || []).map((c) => (
            <button type="button" key={c.id} data-testid={`clinic-access-${c.id}`} onClick={() => onChange({ clinic_ids: toggle(c.id), all_clinics: false })}
              className={`text-sm rounded-full px-3 py-1 border transition-colors ${clinicIds.includes(c.id) ? "bg-clinical text-white border-clinical" : "border-line text-muted2 hover:border-clinical/40"}`}>{c.name}</button>
          ))}
          {(clinics || []).length === 0 && <span className="text-sm text-muted2">No clinics configured.</span>}
        </div>
      )}
    </div>
  );
}

function CreateUserForm({ newUser, setNewUser, creating, onSubmit, onGenPassword, creatableRoles, onCancel, clinics }) {
  return (
    <form onSubmit={onSubmit} data-testid="create-user-form" className="bg-white border border-line rounded-md p-4 mb-6 grid gap-3 sm:grid-cols-2">
      <div className="sm:col-span-2 text-sm font-semibold text-ink">Create an account (active immediately)</div>
      <input data-testid="new-user-first-name" required placeholder="First name" value={newUser.first_name} onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })} className={inp} />
      <input data-testid="new-user-surname" placeholder="Surname" value={newUser.surname} onChange={(e) => setNewUser({ ...newUser, surname: e.target.value })} className={inp} />
      <select data-testid="new-user-role" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} className={inp}>
        {creatableRoles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
      </select>
      <input data-testid="new-user-username" placeholder="Username (optional)" value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} className={inp} />
      <input data-testid="new-user-email" type="email" placeholder="Email (optional)" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className={inp} />
      <input data-testid="new-user-phone" required={newUser.role === "language_interpreter"} placeholder={newUser.role === "language_interpreter" ? "Phone e.g. +263771234567 (required)" : "Phone e.g. +263771234567 (optional)"} value={newUser.phone} onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })} className={`${inp} sm:col-span-2`} />
      {newUser.role === "admin" && (
        <ClinicAccessFields clinics={clinics} clinicIds={newUser.clinic_ids} allClinics={newUser.all_clinics}
          onChange={({ clinic_ids, all_clinics }) => setNewUser({ ...newUser, clinic_ids, all_clinics })} />
      )}
      {(newUser.role === "clinical_specialist" || newUser.role === "language_interpreter") && (
        <div className="sm:col-span-2 border border-line rounded-md p-3 bg-sand/40">
          <LanguageFields languages={newUser.languages} interpreterLanguages={newUser.interpreter_languages}
            showInterpreter={newUser.role !== "language_interpreter"} verifiedOnly={newUser.role === "language_interpreter"}
            onChange={({ languages, interpreter_languages }) => setNewUser({ ...newUser, languages, interpreter_languages })} />
        </div>
      )}
      <div className="sm:col-span-2 flex gap-2">
        <input data-testid="new-user-password" type="text" placeholder="Password (min 6 chars)" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className={inp} />
        <button type="button" onClick={onGenPassword} data-testid="gen-password-btn" className="whitespace-nowrap text-sm font-semibold text-clinical border border-clinical/30 rounded-md px-3 py-2 hover:bg-clinical/5">Generate</button>
      </div>
      <p className="sm:col-span-2 text-xs text-muted2">Provide a username or an email (either can be used to sign in). A phone number enables SMS delivery. Patients receive an appointment code automatically. Credentials are auto-sent by email/SMS where available.</p>
      <div className="sm:col-span-2 flex gap-2">
        <button type="submit" disabled={creating} data-testid="create-user-submit-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-calm rounded-md px-4 py-2 disabled:opacity-60">{creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Create account</button>
        <button type="button" onClick={onCancel} data-testid="cancel-create-user-btn" className="text-sm font-semibold text-muted2 border border-line rounded-md px-4 py-2">Cancel</button>
      </div>
    </form>
  );
}

function UserRow({ u, me, clinics, canManage, assignableRoles, editing, editData, setEditData, onOpenEdit, onSaveEdit,
  resetting, resetPw, setResetPw, resetNotify, setResetNotify, onGenResetPw, onDoReset, onOpenReset, onSetStatus, onRemove, selected, onToggleSelect }) {
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgBody, setMsgBody] = useState("");
  const [sending, setSending] = useState(false);
  const sendMessage = async () => {
    if (!msgBody.trim()) return toast.error("Type a message first.");
    setSending(true);
    try {
      await api.post("/messages", { to_user_id: u.id, body: msgBody.trim() });
      toast.success(`Message sent to ${u.name || u.username}'s inbox`);
      setMsgBody(""); setMsgOpen(false);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSending(false); }
  };
  return (
    <div data-testid="user-row" className="bg-white border border-line rounded-md p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-start gap-3">
        <input type="checkbox" data-testid="user-select" checked={!!selected} onChange={() => onToggleSelect(u.id)} className="mt-1.5" />
        <div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-heading font-bold text-ink">{u.name}</span>
          <span className="text-xs font-bold uppercase px-2 py-0.5 rounded text-clinical bg-clinical/10">{ROLE_LABEL[u.role] || u.role}</span>
          <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${STATUS_STYLE[u.status] || ""}`}>{u.status}</span>
          {u.appointment_code && <span data-testid="user-patient-code" className="text-xs font-bold uppercase px-2 py-0.5 rounded bg-sand text-ink border border-line">{u.appointment_code}</span>}
        </div>
        <p className="text-sm text-muted2 mt-1">{[u.username && `@${u.username}`, u.email, u.phone].filter(Boolean).join(" · ") || "—"} · {u.auth_provider}</p>
        {u.created_at && <p className="text-xs text-muted2 mt-1 inline-flex items-center gap-1.5" data-testid="user-created-at"><Clock className="w-3.5 h-3.5" /> Account created {fmtStamp(u.created_at)}</p>}
        {u.password_changed_at && <p className="text-xs text-muted2 mt-1 inline-flex items-center gap-1.5" data-testid="user-password-changed-at"><KeyRound className="w-3.5 h-3.5" /> Password last changed {fmtStamp(u.password_changed_at)}</p>}
        {(u.role === "clinical_specialist" || u.role === "language_interpreter") && (
          <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="user-languages">
            {(u.languages || []).length > 0 ? (
              <span className="text-xs inline-flex items-center gap-1 bg-clinical/10 text-clinical rounded-full px-2 py-0.5" data-testid="user-spoken-languages">
                <Languages className="w-3 h-3" /> {u.role === "language_interpreter" ? "Interprets" : "Speaks"}: {(u.languages || []).join(", ")}
              </span>
            ) : (
              <span className="text-xs text-muted2 italic">No languages recorded</span>
            )}
            {(u.interpreter_languages || []).length > 0 && (
              <span className="text-xs inline-flex items-center gap-1 bg-amber-100 text-amber-700 rounded-full px-2 py-0.5" data-testid="user-interpreter-needs">
                <Languages className="w-3 h-3" /> Interpreter needed for: {(u.interpreter_languages || []).join(", ")}
              </span>
            )}
          </div>
        )}
      </div>
      </div>
      {canManage ? (
        <div className="flex flex-wrap gap-2">
          {u.status === "pending" && (
            <button onClick={() => onSetStatus(u, "active")} data-testid="user-approve-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-white bg-calm rounded-md px-3 py-1.5"><Check className="w-4 h-4" /> Approve</button>
          )}
          {u.status === "active" && !u.protected && (
            <button onClick={() => onSetStatus(u, "deactivated")} data-testid="user-deactivate-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-muted2 border border-line rounded-md px-3 py-1.5"><Power className="w-4 h-4" /> Deactivate</button>
          )}
          {(u.status === "deactivated" || u.status === "revoked") && (
            <button onClick={() => onSetStatus(u, "active")} data-testid="user-reactivate-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-white bg-calm rounded-md px-3 py-1.5"><Power className="w-4 h-4" /> Reactivate</button>
          )}
          {u.status !== "revoked" && !u.protected && (
            <button onClick={() => onSetStatus(u, "revoked")} data-testid="user-revoke-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-critical border border-critical/30 rounded-md px-3 py-1.5"><Ban className="w-4 h-4" /> Revoke</button>
          )}
          <button onClick={() => setMsgOpen((v) => !v)} data-testid="user-message-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-clinical border border-clinical/30 rounded-md px-3 py-1.5 hover:bg-clinical/5"><MessageCircle className="w-4 h-4" /> Message</button>
          <button onClick={() => onOpenEdit(u)} data-testid="user-edit-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-clinical border border-clinical/30 rounded-md px-3 py-1.5 hover:bg-clinical/5"><Pencil className="w-4 h-4" /> Edit</button>
          <button onClick={() => onOpenReset(u)} data-testid="user-reset-pw-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-clinical border border-clinical/30 rounded-md px-3 py-1.5 hover:bg-clinical/5"><KeyRound className="w-4 h-4" /> Reset PW</button>
          {u.protected ? (
            <span data-testid="user-protected-badge" className="inline-flex items-center gap-1 text-xs font-semibold text-muted2 border border-line rounded-md px-3 py-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Permanent test account</span>
          ) : (
            <button onClick={() => onRemove(u)} data-testid="user-delete-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-critical border border-critical/30 rounded-md px-3 py-1.5"><Trash2 className="w-4 h-4" /> Delete</button>
          )}
        </div>
      ) : (
        <span className="text-xs text-muted2 inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> {u.id === me.id ? "You" : "No permission"}</span>
      )}
      </div>
      {msgOpen && (
        <div data-testid="user-message-form" className="mt-3 pt-3 border-t border-line flex flex-wrap items-center gap-2">
          <input data-testid="user-message-input" placeholder={`Message to ${u.name || u.username}…`} value={msgBody} onChange={(e) => setMsgBody(e.target.value)} className={`${inp} flex-1 min-w-[220px]`} />
          <button type="button" onClick={sendMessage} disabled={sending} data-testid="user-message-send-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-white bg-clinical rounded-md px-4 py-2 disabled:opacity-50">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />} Send to inbox</button>
        </div>
      )}
      {editing && (
        <div data-testid="edit-user-form" className="mt-3 pt-3 border-t border-line grid gap-2 sm:grid-cols-2">
          <input data-testid="edit-user-first-name" placeholder="First name" value={editData.first_name ?? ""} onChange={(e) => setEditData({ ...editData, first_name: e.target.value })} className={inp} />
          <input data-testid="edit-user-surname" placeholder="Surname" value={editData.surname ?? ""} onChange={(e) => setEditData({ ...editData, surname: e.target.value })} className={inp} />
          <select data-testid="edit-user-role" value={editData.role} onChange={(e) => setEditData({ ...editData, role: e.target.value })} className={inp} disabled={u.role === "system_admin"}>
            {[...assignableRoles, ...(assignableRoles.some((r) => r.value === u.role) ? [] : [{ value: u.role, label: ROLE_LABEL[u.role] || u.role }])].map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          <input data-testid="edit-user-username" placeholder="Username" value={editData.username} onChange={(e) => setEditData({ ...editData, username: e.target.value })} className={inp} />
          <input data-testid="edit-user-email" type="email" placeholder="Email" value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })} className={inp} />
          <input data-testid="edit-user-phone" required={editData.role === "language_interpreter"} placeholder={editData.role === "language_interpreter" ? "Phone (required, E.164)" : "WhatsApp number (E.164)"} value={editData.phone} onChange={(e) => setEditData({ ...editData, phone: e.target.value })} className={inp} />
          {editData.role === "admin" && (
            <ClinicAccessFields clinics={clinics} clinicIds={editData.clinic_ids} allClinics={editData.all_clinics}
              onChange={({ clinic_ids, all_clinics }) => setEditData({ ...editData, clinic_ids, all_clinics })} />
          )}
          {(editData.role === "clinical_specialist" || editData.role === "language_interpreter") && (
            <div className="sm:col-span-2 border border-line rounded-md p-3 bg-sand/40">
              <LanguageFields languages={editData.languages || []} interpreterLanguages={editData.interpreter_languages || []}
                showInterpreter={editData.role !== "language_interpreter"} verifiedOnly={editData.role === "language_interpreter"}
                onChange={({ languages, interpreter_languages }) => setEditData({ ...editData, languages, interpreter_languages })} />
            </div>
          )}
          <div className="sm:col-span-2 flex gap-2">
            <button type="button" onClick={() => onSaveEdit(u)} data-testid="edit-user-save-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-white bg-calm rounded-md px-4 py-2"><Check className="w-4 h-4" /> Save changes</button>
            <button type="button" onClick={() => onOpenEdit(u)} className="text-sm font-semibold text-muted2 border border-line rounded-md px-4 py-2">Cancel</button>
          </div>
        </div>
      )}
      {resetting && (
        <div data-testid="reset-pw-form" className="mt-3 pt-3 border-t border-line flex flex-wrap items-center gap-2">
          <input data-testid="reset-pw-input" type="text" placeholder="New password (min 6)" value={resetPw} onChange={(e) => setResetPw(e.target.value)} className={`${inp} max-w-xs`} />
          <button type="button" onClick={onGenResetPw} data-testid="reset-pw-gen-btn" className="text-sm font-semibold text-clinical border border-clinical/30 rounded-md px-3 py-2 hover:bg-clinical/5">Generate</button>
          <label className="flex items-center gap-1.5 text-sm text-muted2 cursor-pointer"><input type="checkbox" data-testid="reset-pw-notify" checked={resetNotify} onChange={(e) => setResetNotify(e.target.checked)} className="w-4 h-4 accent-clinical" /> Notify user (email/SMS)</label>
          <button type="button" onClick={() => onDoReset(u)} data-testid="reset-pw-save-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-white bg-calm rounded-md px-4 py-2"><Check className="w-4 h-4" /> Save</button>
          <button type="button" onClick={() => onOpenReset(u)} className="text-sm font-semibold text-muted2 border border-line rounded-md px-4 py-2">Cancel</button>
        </div>
      )}
    </div>
  );
}

export function AdminUsers({ me, onChange }) {
  const [users, setUsers] = useState([]);
  const { clinics } = useAppData();
  const ROLE_LEVEL = { public: 0, language_interpreter: 1, clinical_specialist: 1, admin: 2, super_admin: 3, system_admin: 4 };
  const blankNew = { name: "", first_name: "", surname: "", role: "clinical_specialist", username: "", email: "", phone: "", password: "", clinic_ids: [], all_clinics: false, languages: [], interpreter_languages: [] };
  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState(blankNew);
  const [creating, setCreating] = useState(false);
  const [resetFor, setResetFor] = useState(null); // user id whose password is being reset
  const [resetPw, setResetPw] = useState("");
  const [resetNotify, setResetNotify] = useState(true);
  const [editFor, setEditFor] = useState(null); // user id being edited
  const [editData, setEditData] = useState({});
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sortBy, setSortBy] = useState("created");
  const [selectedIds, setSelectedIds] = useState([]);
  const toggleSelect = (id) => setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const USER_COLS = [
    { key: "name", label: "Name" }, { key: "role", label: "Role" }, { key: "username", label: "Username" },
    { key: "email", label: "Email" }, { key: "phone", label: "WhatsApp" }, { key: "appointment_code", label: "Patient Code" },
    { key: "created", label: "Created" },
  ];
  const exportRows = (list) => list.map((u) => ({ ...u, role: ROLE_LABEL[u.role] || u.role, created: u.created_at ? fmtStamp(u.created_at) : "" }));
  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/users"); setUsers(data); }
    catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Roles the current actor may create. System Admin may create ANY role; others only roles below their level.
  const creatableRoles = [
    { value: "public", label: "Basic User (public)" },
    { value: "clinical_specialist", label: "Clinical Specialist" },
    { value: "language_interpreter", label: "Language Interpreter" },
    { value: "admin", label: "Administrator" },
    { value: "super_admin", label: "Super Admin" },
    { value: "system_admin", label: "System Admin" },
  ].filter((r) => {
    if (r.value === "language_interpreter") return me.role === "system_admin" || me.role === "super_admin";
    return me.role === "system_admin" || ROLE_LEVEL[r.value] < ROLE_LEVEL[me.role]
      || (me.role === "super_admin" && r.value === "super_admin");
  });

  // Roles the actor may assign when editing (excludes system_admin unless actor is system_admin).
  const assignableRoles = creatableRoles;

  const displayed = users
    .filter((u) => roleFilter === "all" || u.role === roleFilter)
    .filter((u) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return [u.name, u.username, u.email, u.appointment_code].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    })
    .sort((a, b) => {
      if (sortBy === "name-az") return (a.name || "").localeCompare(b.name || "");
      if (sortBy === "name-za") return (b.name || "").localeCompare(a.name || "");
      if (sortBy === "code") return (a.appointment_code || "~").localeCompare(b.appointment_code || "~");
      return String(b.created_at || "").localeCompare(String(a.created_at || "")); // created (newest first)
    });

  const genPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
    let p = "";
    for (let i = 0; i < 12; i++) p += chars[Math.floor(Math.random() * chars.length)];
    setNewUser((n) => ({ ...n, password: p }));
    toast.info("Temporary password generated — copy & share it securely.");
  };
  const genResetPw = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
    let p = "";
    for (let i = 0; i < 12; i++) p += chars[Math.floor(Math.random() * chars.length)];
    setResetPw(p);
  };
  const doResetPassword = async (u) => {
    if (resetPw.length < 6) return toast.error("Password must be at least 6 characters.");
    try {
      const { data } = await api.put(`/admin/users/${u.id}/password`, { new_password: resetPw, notify: resetNotify });
      const sent = data.notified?.length ? ` · sent via ${data.notified.join(" & ")}` : "";
      toast.success(`Password reset for ${u.name}${sent}`);
      setResetFor(null); setResetPw("");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const createUser = async (e) => {
    e.preventDefault();
    if (!newUser.username.trim() && !newUser.email.trim()) return toast.error("Provide a username or an email.");
    if (newUser.password.length < 6) return toast.error("Password must be at least 6 characters.");
    if (newUser.role === "language_interpreter" && !newUser.phone.trim()) return toast.error("A telephone number is mandatory for interpreter accounts.");
    if (newUser.role === "language_interpreter" && !(newUser.languages || []).length) return toast.error("Select at least one verified language for the interpreter.");
    setCreating(true);
    try {
      const payload = {
        first_name: newUser.first_name.trim() || undefined,
        surname: newUser.surname.trim() || undefined,
        name: `${newUser.first_name.trim()} ${newUser.surname.trim()}`.trim() || undefined,
        role: newUser.role,
        password: newUser.password,
        username: newUser.username.trim() || undefined,
        email: newUser.email.trim() || undefined,
        phone: newUser.phone.trim() || undefined,
      };
      if (newUser.role === "admin") { payload.clinic_ids = newUser.clinic_ids; payload.all_clinics = newUser.all_clinics; }
      if (newUser.role === "clinical_specialist" || newUser.role === "language_interpreter") { payload.languages = newUser.languages; payload.interpreter_languages = newUser.interpreter_languages; }
      const { data } = await api.post("/admin/users", payload);
      const sent = data.notified?.length ? ` · sent via ${data.notified.join(" & ")}` : "";
      toast.success(`Account created for ${data.name}${data.appointment_code ? ` · code ${data.appointment_code}` : ""}${sent}`);
      setNewUser(blankNew); setShowCreate(false); load(); onChange?.();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setCreating(false); }
  };

  const canManage = (u) => (ROLE_LEVEL[me.role] > ROLE_LEVEL[u.role] || me.role === "system_admin") && u.id !== me.id;

  const openEdit = (u) => {
    setEditFor(editFor === u.id ? null : u.id);
    const parts = (u.name || "").trim().split(/\s+/);
    setEditData({ name: u.name || "", first_name: u.first_name ?? (parts[0] || ""), surname: u.surname ?? (parts.slice(1).join(" ") || ""), username: u.username || "", email: u.email || "", phone: u.phone || "", role: u.role, clinic_ids: u.clinic_ids || [], all_clinics: !!u.all_clinics, languages: u.languages || [], interpreter_languages: u.interpreter_languages || [] });
  };
  const openReset = (u) => { setResetFor(resetFor === u.id ? null : u.id); setResetPw(""); setResetNotify(true); };
  const saveEdit = async (u) => {
    try {
      const payload = {
        first_name: (editData.first_name || "").trim() || null,
        surname: (editData.surname || "").trim() || null,
        username: editData.username.trim() || null,
        email: editData.email.trim() || null,
        phone: editData.phone.trim() || null,
        role: editData.role,
      };
      if (editData.role === "admin") { payload.clinic_ids = editData.clinic_ids || []; payload.all_clinics = !!editData.all_clinics; }
      if (editData.role === "clinical_specialist" || editData.role === "language_interpreter") { payload.languages = editData.languages || []; payload.interpreter_languages = editData.interpreter_languages || []; }
      await api.put(`/admin/users/${u.id}`, payload);
      toast.success("Account updated"); setEditFor(null); load(); onChange?.();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const setStatus = async (u, status) => {
    try { await api.put(`/admin/users/${u.id}/status`, { status }); toast.success("Updated"); load(); onChange?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const removeUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name} (${u.email || u.username})? This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User deleted"); load(); onChange?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const revokedCount = users.filter((u) => u.status === "revoked").length;
  const bulkDeleteRevoked = async () => {
    if (!window.confirm(`Permanently delete all ${revokedCount} revoked account(s)? This cannot be undone.`)) return;
    try { const { data } = await api.post("/admin/users/bulk-delete", { status: "revoked" }); toast.success(`${data.deleted} account(s) deleted`); load(); onChange?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-heading font-bold text-3xl tracking-tight text-ink flex items-center gap-2"><UserCog className="w-7 h-7 text-clinical" /> User Accounts</h1>
        <div className="flex items-center gap-2">
          {creatableRoles.length > 0 && (
            <button onClick={() => setShowCreate((s) => !s)} data-testid="toggle-create-user-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-clinical rounded-md px-3 py-2 hover:bg-clinicaldark transition-colors"><Plus className="w-4 h-4" /> Add user</button>
          )}
          {revokedCount > 0 && (
            <button onClick={bulkDeleteRevoked} data-testid="bulk-delete-revoked-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-critical border border-critical/30 rounded-md px-3 py-2 hover:bg-critical/5 transition-colors"><Trash2 className="w-4 h-4" /> Delete all revoked ({revokedCount})</button>
          )}
        </div>
      </div>

      {showCreate && creatableRoles.length > 0 && (
        <CreateUserForm
          newUser={newUser}
          setNewUser={setNewUser}
          creating={creating}
          onSubmit={createUser}
          onGenPassword={genPassword}
          creatableRoles={creatableRoles}
          clinics={clinics}
          onCancel={() => { setShowCreate(false); setNewUser(blankNew); }}
        />
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input data-testid="user-search" placeholder="Search name, username or code…" value={search} onChange={(e) => setSearch(e.target.value)} className={`${inp} max-w-xs`} />
        <select data-testid="user-role-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className={`${inp} max-w-[190px]`}>
          <option value="all">All account types</option>
          <option value="system_admin">System Admin</option>
          <option value="super_admin">Super Admin</option>
          <option value="admin">Administrator</option>
          <option value="clinical_specialist">Clinical Specialist</option>
          <option value="public">Basic User</option>
        </select>
        <select data-testid="user-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={`${inp} max-w-[190px]`}>
          <option value="created">Date created (newest)</option>
          <option value="code">Code</option>
          <option value="name-az">Name (A–Z)</option>
          <option value="name-za">Name (Z–A)</option>
        </select>
        <span className="text-sm text-muted2" data-testid="user-count">{displayed.length} of {users.length}</span>
        {(() => { const sel = selectedIds.length ? displayed.filter((u) => selectedIds.includes(u.id)) : displayed; return (
          <>
            <button onClick={() => exportExcel("users", USER_COLS, exportRows(sel))} data-testid="users-export-excel" className="inline-flex items-center gap-1.5 border border-line rounded-md px-3 py-1.5 text-sm font-semibold text-calm"><FileSpreadsheet className="w-4 h-4" /> Excel{selectedIds.length ? ` (${selectedIds.length})` : ""}</button>
            <button onClick={() => exportPDF("Users", USER_COLS, exportRows(sel))} data-testid="users-export-pdf" className="inline-flex items-center gap-1.5 border border-line rounded-md px-3 py-1.5 text-sm font-semibold text-critical"><FileText className="w-4 h-4" /> PDF{selectedIds.length ? ` (${selectedIds.length})` : ""}</button>
          </>
        ); })()}
      </div>

      <div className="grid gap-3" data-testid="users-list">
        {displayed.map((u) => (
          <UserRow
            key={u.id}
            u={u}
            me={me}
            clinics={clinics}
            canManage={canManage(u)}
            assignableRoles={assignableRoles}
            editing={editFor === u.id}
            editData={editData}
            setEditData={setEditData}
            onOpenEdit={openEdit}
            onSaveEdit={saveEdit}
            resetting={resetFor === u.id}
            resetPw={resetPw}
            setResetPw={setResetPw}
            resetNotify={resetNotify}
            setResetNotify={setResetNotify}
            onGenResetPw={genResetPw}
            onDoReset={doResetPassword}
            onOpenReset={openReset}
            onSetStatus={setStatus}
            onRemove={removeUser}
            selected={selectedIds.includes(u.id)}
            onToggleSelect={toggleSelect}
          />
        ))}
      </div>
    </div>
  );
}

/* ---------------- Clinics (FR-004) ---------------- */
export function AdminClinics() {
  const { clinics, refresh } = useAppData();
  const blank = { name: "", location: "", description: "", phone: "", services: "", photo: "", map_url: "", active: true };
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo: data.url })); toast.success("Photo uploaded");
    } catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const payload = {
      ...form,
      services: form.services.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editId) await api.put(`/clinics/${editId}`, payload);
      else await api.post("/clinics", payload);
      toast.success("Saved"); setForm(blank); setEditId(null); refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/clinics/${id}`); toast.success("Deleted"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const photoSrc = form.photo ? (form.photo.startsWith("http") ? form.photo : `${process.env.REACT_APP_BACKEND_URL}${form.photo}`) : "";

  return (
    <div>
      <h1 className="font-heading font-bold text-3xl tracking-tight text-ink mb-6 flex items-center gap-2"><Building2 className="w-7 h-7 text-clinical" /> Clinics</h1>
      <div className="bg-white border border-line rounded-md p-4 mb-6 grid sm:grid-cols-2 gap-3" data-testid="clinic-form">
        <input className={inp} placeholder="Clinic name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="clinic-name-input" />
        <input className={inp} placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="clinic-location-input" />
        <input className={inp} placeholder="Phone (e.g. +263 77 123 4567)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="clinic-phone-input" />
        <input className={inp} placeholder="Services (comma-separated)" value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} data-testid="clinic-services-input" />
        <input className={`${inp} sm:col-span-2`} placeholder="Map link (Google Maps URL — optional)" value={form.map_url} onChange={(e) => setForm({ ...form, map_url: e.target.value })} data-testid="clinic-map-input" />
        <input className={`${inp} sm:col-span-2`} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <div className="sm:col-span-2 flex items-center gap-3">
          {photoSrc ? <img src={photoSrc} alt="" className="w-20 h-14 rounded object-cover border border-line" data-testid="clinic-photo-preview" /> : <div className="w-20 h-14 rounded bg-sand border border-line flex items-center justify-center"><Building2 className="w-5 h-5 text-muted2" /></div>}
          <label className="inline-flex items-center gap-1.5 text-sm font-semibold text-clinical border border-clinical/30 rounded-md px-3 py-2 cursor-pointer hover:bg-clinical/5">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} {form.photo ? "Change photo" : "Upload photo"}
            <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="clinic-photo-input" />
          </label>
          {form.photo && <button type="button" onClick={() => setForm({ ...form, photo: "" })} className="text-sm text-critical font-semibold">Remove</button>}
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
        <button onClick={save} data-testid="clinic-save-btn" className="inline-flex items-center justify-center gap-1.5 bg-clinical text-white rounded-md px-4 py-2 text-sm font-semibold"><Plus className="w-4 h-4" /> {editId ? "Update" : "Add clinic"}</button>
      </div>
      <div className="grid gap-3">
        {clinics.map((c) => (
          <div key={c.id} data-testid="clinic-row" className="bg-white border border-line rounded-md p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {c.photo && <img src={c.photo.startsWith("http") ? c.photo : `${process.env.REACT_APP_BACKEND_URL}${c.photo}`} alt="" className="w-16 h-12 rounded object-cover border border-line shrink-0" />}
              <div className="min-w-0">
                <span className="font-heading font-bold text-ink">{c.name}</span> {!c.active && <span className="text-xs text-muted2">(inactive)</span>}
                <p className="text-sm text-muted2">{[c.location, c.phone].filter(Boolean).join(" · ")}</p>
                {c.services?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {c.services.map((s) => <span key={s} className="text-xs font-semibold bg-clinical/10 text-clinical rounded-full px-2 py-0.5">{s}</span>)}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => { setForm({ name: c.name, location: c.location || "", description: c.description || "", phone: c.phone || "", services: (c.services || []).join(", "), photo: c.photo || "", map_url: c.map_url || "", active: c.active }); setEditId(c.id); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="text-sm text-clinical font-semibold px-3 py-1.5">Edit</button>
              <button onClick={() => del(c.id)} data-testid="clinic-delete-btn" className="text-critical px-2"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Categories (FR-005) ---------------- */
export function AdminCategories() {
  const { categories, refresh } = useAppData();
  const [form, setForm] = useState({ kind: "announcement", name: "" });

  const add = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try { await api.post("/categories", { ...form, active: true }); toast.success("Added"); setForm({ ...form, name: "" }); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggle = async (c) => {
    try { await api.put(`/categories/${c.id}`, { kind: c.kind, name: c.name, active: !c.active }); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const rename = async (c) => {
    const name = prompt("Rename category", c.name);
    if (!name) return;
    try { await api.put(`/categories/${c.id}`, { kind: c.kind, name, active: c.active }); toast.success("Renamed"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (c) => {
    try { await api.delete(`/categories/${c.id}`); toast.success("Deleted"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const kinds = ["announcement", "inventory", "event"];
  const catsByKind = useMemo(() => {
    const m = { announcement: [], inventory: [], event: [] };
    categories.forEach((c) => { (m[c.kind] = m[c.kind] || []).push(c); });
    return m;
  }, [categories]);
  return (
    <div>
      <h1 className="font-heading font-bold text-3xl tracking-tight text-ink mb-6">Categories</h1>
      <div className="bg-white border border-line rounded-md p-4 mb-6 flex flex-wrap gap-3 items-end" data-testid="category-form">
        <select className={`${inp} w-auto`} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="category-kind-select">
          {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <input className={`${inp} flex-1 min-w-[180px]`} placeholder="New category name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="category-name-input" />
        <button onClick={add} data-testid="category-add-btn" className="inline-flex items-center gap-1.5 bg-clinical text-white rounded-md px-4 py-2 text-sm font-semibold"><Plus className="w-4 h-4" /> Add</button>
      </div>
      {kinds.map((k) => (
        <div key={k} className="mb-6">
          <h2 className="font-heading font-bold text-lg text-ink capitalize mb-2">{k} categories</h2>
          <div className="flex flex-wrap gap-2">
            {(catsByKind[k] || []).map((c) => (
              <div key={c.id} data-testid="category-chip" className={`inline-flex items-center gap-2 border rounded-full pl-3 pr-1.5 py-1 text-sm ${c.active ? "border-line bg-white" : "border-line bg-line/40 text-muted2"}`}>
                <button onClick={() => rename(c)} className="font-medium">{c.name}</button>
                <button onClick={() => toggle(c)} title="Toggle active" className="text-xs px-1.5 rounded bg-clinical/10 text-clinical">{c.active ? "on" : "off"}</button>
                <button onClick={() => del(c)} className="text-critical"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Banners (FR-002) ---------------- */
export function AdminBanners() {
  const { banners, refresh } = useAppData();
  const [busy, setBusy] = useState(false);
  const blank = { title: "", description: "", image_url: "", order: banners.length };
  const [form, setForm] = useState(blank);

  const uploadImg = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setForm((f) => ({ ...f, image_url: data.url })); toast.success("Image uploaded"); }
    catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  const add = async () => {
    if (!form.image_url) return toast.error("Add an image (upload or URL)");
    try { await api.post("/banners", form); toast.success("Banner added"); setForm(blank); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const update = async (b, patch) => {
    try { await api.put(`/banners/${b.id}`, { title: b.title, description: b.description, image_url: b.image_url, order: b.order, ...patch }); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { try { await api.delete(`/banners/${id}`); toast.success("Removed"); refresh(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div>
      <h1 className="font-heading font-bold text-3xl tracking-tight text-ink mb-2 flex items-center gap-2"><ImageIcon className="w-7 h-7 text-clinical" /> Rotating Banner</h1>
      <p className="text-muted2 mb-6 text-sm">Recommended: 3 images. They rotate automatically on the public site & app.</p>

      <div className="bg-white border border-line rounded-md p-4 mb-6 space-y-3" data-testid="banner-form">
        <input className={inp} placeholder="Banner title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="banner-title-input" />
        <input className={inp} placeholder="Banner description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <div className="flex flex-wrap gap-2 items-center">
          <input className={`${inp} flex-1 min-w-[200px]`} placeholder="Image URL or upload →" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="banner-image-input" />
          <label className="inline-flex items-center gap-1.5 text-sm font-semibold text-clinical border border-clinical/30 rounded-md px-3 py-2 cursor-pointer">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} Upload
            <input type="file" accept="image/*" className="hidden" onChange={uploadImg} />
          </label>
          <input type="number" className={`${inp} w-20`} value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} title="Order" />
          <button onClick={add} data-testid="banner-add-btn" className="inline-flex items-center gap-1.5 bg-clinical text-white rounded-md px-4 py-2 text-sm font-semibold"><Plus className="w-4 h-4" /> Add</button>
        </div>
      </div>

      <div className="grid gap-3" data-testid="banner-list">
        {banners.map((b) => (
          <div key={b.id} data-testid="banner-row" className="bg-white border border-line rounded-md p-3 flex gap-3 items-center">
            <img src={/^https?:/.test(b.image_url) ? b.image_url : `${process.env.REACT_APP_BACKEND_URL}${b.image_url}`} alt="" className="w-28 h-16 object-cover rounded" />
            <div className="flex-1 min-w-0 space-y-1">
              <input className={inp} defaultValue={b.title} onBlur={(e) => update(b, { title: e.target.value })} />
              <input className={inp} defaultValue={b.description} onBlur={(e) => update(b, { description: e.target.value })} />
            </div>
            <input type="number" className={`${inp} w-16`} defaultValue={b.order} onBlur={(e) => update(b, { order: Number(e.target.value) })} title="Order" />
            <button onClick={() => del(b.id)} data-testid="banner-delete-btn" className="text-critical px-2"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Settings / WhatsApp (FR-003) ---------------- */
export function AdminSettings({ onChange, onBookDay }) {
  const [s, setS] = useState({ whatsapp_number: "", whatsapp_active: false });
  const { refresh } = useAppData();
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put("/admin/settings", s); toast.success("Settings saved"); refresh(); onChange?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const sendTest = async () => {
    if (!testPhone.trim()) return toast.error("Enter a WhatsApp number, e.g. +447789380777");
    setTesting(true);
    try {
      const { data } = await api.post("/admin/whatsapp/test", { phone: testPhone.trim() });
      if (data.sent) toast.success(data.detail);
      else toast.message(data.detail || "WhatsApp not sent.");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setTesting(false); }
  };
  const [langs, setLangs] = useState([]);
  const [newLang, setNewLang] = useState("");
  const loadLangs = async () => { try { const { data } = await api.get("/languages"); setLangs(data); } catch { /* ignore */ } };
  useEffect(() => { loadLangs(); }, []);
  const addLang = async () => {
    if (!newLang.trim()) return;
    try { await api.post("/admin/languages", { name: newLang.trim() }); setNewLang(""); loadLangs(); toast.success("Language added"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delLang = async (id) => { try { await api.delete(`/admin/languages/${id}`); loadLangs(); } catch (e) { toast.error(apiErr(e)); } };
  const toggleVerify = async (id) => { try { await api.put(`/admin/languages/${id}/verify`); loadLangs(); } catch (e) { toast.error(apiErr(e)); } };
  const [caRows, setCaRows] = useState([]);
  useEffect(() => { api.get("/admin/clinic-access").then(({ data }) => setCaRows(data)).catch(() => {}); }, []);
  const CA_COLS = [{ key: "name", label: "Admin" }, { key: "username", label: "Username" }, { key: "email", label: "Email" }, { key: "clinicsText", label: "Clinics" }];
  const caExport = () => caRows.map((r) => ({ ...r, clinicsText: (r.clinics || []).join(", ") }));
  return (
    <div className="max-w-lg">
      <h1 className="font-heading font-bold text-3xl tracking-tight text-ink mb-6 flex items-center gap-2"><MessageCircle className="w-7 h-7 text-[#25D366]" /> WhatsApp Contact</h1>
      <div className="bg-white border border-line rounded-md p-5 space-y-4" data-testid="settings-form">
        <label className="block">
          <span className="text-xs uppercase tracking-wider font-bold text-muted2">Official WhatsApp number (with country code)</span>
          <input className={`${inp} mt-1.5`} placeholder="+265 999 000 111" value={s.whatsapp_number || ""} onChange={(e) => setS({ ...s, whatsapp_number: e.target.value })} data-testid="whatsapp-number-input" />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={s.whatsapp_active} onChange={(e) => setS({ ...s, whatsapp_active: e.target.checked })} data-testid="whatsapp-active-toggle" />
          Enable WhatsApp contact platform-wide
        </label>
        <p className="text-xs text-muted2">The number is never shown publicly — visitors are routed privately through a secure link when an announcement enables WhatsApp.</p>
        <div className="border-t border-line pt-4">
          <label className="block">
            <span className="text-xs uppercase tracking-wider font-bold text-muted2">Voice note retention (days)</span>
            <input type="number" min="1" max="3650" className={`${inp} mt-1.5`} placeholder="90"
              value={s.voice_note_retention_days ?? 90}
              onChange={(e) => setS({ ...s, voice_note_retention_days: e.target.value === "" ? "" : Math.max(1, Math.min(3650, parseInt(e.target.value) || 0)) })}
              data-testid="voice-retention-input" />
          </label>
          <p className="text-xs text-muted2 mt-1.5">General Enquiries voice notes are auto-deleted after this many days to save storage. The written transcript is always kept.</p>
        </div>
        <div className="border-t border-line pt-4">
          <span className="text-xs uppercase tracking-wider font-bold text-muted2">Voice recording limits (seconds)</span>
          <p className="text-xs text-muted2 mt-1 mb-3">Maximum length for each kind of voice note. Recording stops automatically at the limit. Between 5 and 300 seconds.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs font-semibold text-ink">Visitor chat &amp; translator</span>
              <input type="number" min="5" max="300" className={`${inp} mt-1`} placeholder="30"
                value={s.voice_max_chat_seconds ?? 30}
                onChange={(e) => setS({ ...s, voice_max_chat_seconds: e.target.value === "" ? "" : Math.max(5, Math.min(300, parseInt(e.target.value) || 0)) })}
                data-testid="voice-max-chat-input" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-ink">Staff enquiry replies</span>
              <input type="number" min="5" max="300" className={`${inp} mt-1`} placeholder="30"
                value={s.voice_max_reply_seconds ?? 30}
                onChange={(e) => setS({ ...s, voice_max_reply_seconds: e.target.value === "" ? "" : Math.max(5, Math.min(300, parseInt(e.target.value) || 0)) })}
                data-testid="voice-max-reply-input" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-ink">Announcements &amp; events</span>
              <input type="number" min="5" max="300" className={`${inp} mt-1`} placeholder="60"
                value={s.voice_max_announcement_seconds ?? 60}
                onChange={(e) => setS({ ...s, voice_max_announcement_seconds: e.target.value === "" ? "" : Math.max(5, Math.min(300, parseInt(e.target.value) || 0)) })}
                data-testid="voice-max-announcement-input" />
            </label>
          </div>
        </div>
        <button onClick={save} data-testid="settings-save-btn" className="inline-flex items-center gap-1.5 bg-clinical text-white rounded-md px-4 py-2 text-sm font-semibold"><Save className="w-4 h-4" /> Save</button>
      </div>

      <div className="bg-white border border-line rounded-md p-5 mt-6" data-testid="whatsapp-test-card">
        <h2 className="font-heading font-bold text-lg text-ink flex items-center gap-2 mb-1"><Send className="w-5 h-5 text-[#25D366]" /> Send a test WhatsApp</h2>
        <p className="text-sm text-muted2 mb-3">Verify the WhatsApp integration before running a real broadcast. On the Twilio Sandbox, the recipient must have joined the sandbox first.</p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            data-testid="whatsapp-test-input"
            type="tel"
            placeholder="+447789380777"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            className={`${inp} flex-1 min-w-[220px]`}
          />
          <button onClick={sendTest} disabled={testing} data-testid="whatsapp-test-send-btn" className="inline-flex items-center gap-1.5 bg-[#25D366] text-white rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-50">
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test
          </button>
        </div>
      </div>

      <div className="bg-white border border-line rounded-md p-5 mt-6" data-testid="languages-card">
        <h2 className="font-heading font-bold text-lg text-ink flex items-center gap-2 mb-1"><Building2 className="w-5 h-5 text-clinical" /> Languages</h2>
        <p className="text-sm text-muted2 mb-3">Manage the languages available for patient preferences and clinician proficiency. Only <b>verified</b> languages can be assigned to interpreters.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {langs.map((l) => (
            <span key={l.id} data-testid="language-chip" className={`inline-flex items-center gap-1.5 text-sm border rounded-full pl-3 pr-1.5 py-1 ${l.verified !== false ? "bg-clinical/10 border-clinical/30" : "bg-sand border-line"}`}>
              {l.name}
              <button onClick={() => toggleVerify(l.id)} data-testid="language-verify-toggle" title={l.verified !== false ? "Verified — click to unverify" : "Not verified — click to verify"}
                className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-semibold ${l.verified !== false ? "text-clinical" : "text-muted2 hover:text-clinical"}`}>
                <BadgeCheck className="w-3.5 h-3.5" /> {l.verified !== false ? "Verified" : "Verify"}
              </button>
              <button onClick={() => delLang(l.id)} data-testid="language-delete" className="rounded-full hover:bg-critical/10 text-critical p-0.5"><Trash2 className="w-3.5 h-3.5" /></button>
            </span>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid="language-input" value={newLang} onChange={(e) => setNewLang(e.target.value)} placeholder="Add a language…" className={`${inp} flex-1 min-w-[180px]`} />
          <button onClick={addLang} data-testid="language-add-btn" className="inline-flex items-center gap-1.5 bg-clinical text-white rounded-md px-4 py-2 text-sm font-semibold"><Plus className="w-4 h-4" /> Add</button>
        </div>
      </div>

      <div className="bg-white border border-line rounded-md p-5 mt-6" data-testid="clinic-access-report">
        <div className="flex items-center justify-between gap-2 mb-1">
          <h2 className="font-heading font-bold text-lg text-ink flex items-center gap-2"><Building2 className="w-5 h-5 text-clinical" /> Clinic Access Report</h2>
          <div className="flex gap-2">
            <button onClick={() => exportExcel("clinic_access", CA_COLS, caExport())} disabled={!caRows.length} data-testid="ca-export-excel" className="inline-flex items-center gap-1.5 border border-line rounded-md px-3 py-1.5 text-sm font-semibold text-calm disabled:opacity-40"><FileSpreadsheet className="w-4 h-4" /> Excel</button>
            <button onClick={() => exportPDF("Clinic Access Report", CA_COLS, caExport())} disabled={!caRows.length} data-testid="ca-export-pdf" className="inline-flex items-center gap-1.5 border border-line rounded-md px-3 py-1.5 text-sm font-semibold text-critical disabled:opacity-40"><FileText className="w-4 h-4" /> PDF</button>
          </div>
        </div>
        <p className="text-sm text-muted2 mb-3">Which Administrator accounts can access which clinics.</p>
        <div className="grid gap-2">
          {caRows.map((r) => (
            <div key={r.user_id} data-testid="ca-row" className="flex flex-wrap items-center justify-between gap-2 border border-line rounded-md px-3 py-2">
              <span className="font-semibold text-ink text-sm">{r.name} <span className="text-muted2 font-normal">{r.username && `@${r.username}`}</span></span>
              <span className="text-xs text-muted2">{(r.clinics || []).join(", ") || "No clinics assigned"}</span>
            </div>
          ))}
          {caRows.length === 0 && <p className="text-sm text-muted2">No administrator accounts.</p>}
        </div>
      </div>

      <InterpreterReports onBookDay={onBookDay} />
    </div>
  );
}
